import { D } from '@mobily/ts-belt';
import HistoryIcon from '@mui/icons-material/History';
import InProgressIcon from '@mui/icons-material/Today';
import AllIcon from '@mui/icons-material/ViewList';
import { useEffect, useMemo, useState } from 'react';
import {
  BooleanInput,
  RecordContextProvider,
  SelectArrayInput,
  TextField,
  TopToolbar,
  useListContext,
  useRemoveFromStore,
  useResourceContext,
} from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';

import { OdOrderStatus } from '@Common/types/b2b/orders';
import {
  BO_EXTERNAL_VISIBLE_INDIVIDUAL_OPERATION_TYPES,
  BO_INTERNAL_VISIBLE_INDIVIDUAL_OPERATION_TYPES,
  GetB2bIndividualOperationsOneResponse,
  GetB2bIndividualOperationsQuery,
  getB2bIndividualOperationsSortColumns,
} from '@Common/types/bo/b2b/individual_operations';

import {
  b2bIndividualOperationInitiatorChoices,
  b2bIndividualOperationTypeChoices,
} from '@sh4res/bo-common/choices/individual_operations';
import {
  b2bCancellationInitiatorChoices,
  b2bCancellationReasonChoices,
  b2bOrderStatusChoices,
} from '@sh4res/bo-common/choices/orders';
import { planCategoryChoices } from '@sh4res/bo-common/choices/plans';
import {
  DatagridTyped,
  EmptyScreen,
  FilterButton,
  List,
  VerticalTab,
  VerticalTabsLayout,
} from '@sh4res/bo-common/components';
import { B2bIndividualOperationCreate } from '@sh4res/bo-common/components/B2bIndividualOperations/B2bIndividualOperationCreate';
import { ExportButtonWithConfirmation } from '@sh4res/bo-common/components/B2bIndividualOperations/ExportButtonWithConfirmation';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import {
  AmountInput,
  CurrencyField,
  IdField,
  SelectChipField,
  TextArrayInput,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { createFilters, FilterList } from '@sh4res/bo-common/utils/create_filters';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

// The cancellation fields are only returned by the BO detail endpoint
type IndividualOperationListRecord = Omit<
  GetB2bIndividualOperationsOneResponse,
  'has_matching_contribution' | 'b2b_correction_id' | 'b2b_correction_status'
>;

const NoIndividualOperations = () => (
  <EmptyScreen
    label="resources.bo_common.b2b.individual_operations.list.empty"
    svg={<EmptyFolderSvg />}
  />
);

type IndividualOperationTab = 'in_progress' | 'history' | 'all';

const statusTabMapping: Record<OdOrderStatus, IndividualOperationTab> = {
  filled: 'history',
  shares_canceled: 'history',
  rejected: 'history',
  pending: 'in_progress',
  pending_funds: 'in_progress',
  shares_new: 'in_progress',
  shares_pending_approval: 'in_progress',
  shares_pending_arbitrage: 'in_progress',
  shares_pending_blocked_instrument: 'in_progress',
  shares_pending_campaign: 'in_progress',
  shares_pending_company: 'in_progress',
  shares_pending_cutoff_period: 'in_progress',
  shares_pending_reallocation: 'in_progress',
  shares_pending_scheduling: 'in_progress',
  shares_pending_triggered_order: 'in_progress',
};

const defaultSort: RaSort<GetB2bIndividualOperationsQuery> = { field: 'id', order: 'DESC' };

const testId = 'BoExternalB2bIndividualOperationsList';

const b2bIndividualOperationsFilters = (statusFilters: OdOrderStatus[], isExternal: boolean) =>
  createFilters<GetB2bIndividualOperationsQuery>({
    id: <TextArrayInput asNumber source="" testId={`${testId}_IdInput`} />,
    equipment: (
      <SelectArrayInput
        choices={planCategoryChoices}
        source=""
        data-testid={`${testId}_EquipmentInput`}
      />
    ),
    type: (
      <SelectArrayInput
        choices={b2bIndividualOperationTypeChoices.filter((choice) =>
          (isExternal
            ? BO_EXTERNAL_VISIBLE_INDIVIDUAL_OPERATION_TYPES
            : BO_INTERNAL_VISIBLE_INDIVIDUAL_OPERATION_TYPES
          ).includes(choice.id),
        )}
        source=""
        data-testid={`${testId}_TypeInput`}
      />
    ),
    initiator: (
      <SelectArrayInput
        choices={b2bIndividualOperationInitiatorChoices}
        source=""
        data-testid={`${testId}_InitiatorInput`}
      />
    ),
    status: (
      <SelectArrayInput
        choices={b2bOrderStatusChoices.filter((choice) => statusFilters.includes(choice.id))}
        source=""
        data-testid={`${testId}_StatusInput`}
      />
    ),
    from_created_at: <DateInput source="" data-testid={`${testId}_FromCreatedAtInput`} />,
    to_created_at: <DateInput source="" data-testid={`${testId}_ToCreatedAtInput`} />,
    b2b_employee_id: <TextArrayInput asNumber source="" testId={`${testId}_B2bEmployeeIdInput`} />,
    b2b_contract_id: <TextArrayInput asNumber source="" testId={`${testId}_B2bContractIdInput`} />,
    b2b_company_plan_id: (
      <TextArrayInput asNumber source="" testId={`${testId}_B2bCompanyPlanIdInput`} />
    ),
    from_total_gross_amount: (
      <AmountInput source="" testId={`${testId}_FromTotalGrossAmountInput`} />
    ),
    to_total_gross_amount: <AmountInput source="" testId={`${testId}_ToTotalGrossAmountInput`} />,
    from_total_net_amount: <AmountInput source="" testId={`${testId}_FromTotalNetAmountInput`} />,
    to_total_net_amount: <AmountInput source="" testId={`${testId}_ToTotalNetAmountInput`} />,
    is_recurring_investment: (
      <BooleanInput source="" data-testid={`${testId}_IsRecurringInvestmentInput`} />
    ),
    ...(!isExternal
      ? {
          b2b_company_id: (
            <TextArrayInput asNumber source="" testId={`${testId}_B2bCompanyIdInput`} />
          ),
          user_id: <TextArrayInput asNumber source="" testId={`${testId}_UserIdInput`} />,
          b2b_recurring_investment_id: (
            <TextArrayInput asNumber source="" testId={`${testId}_B2bRecurringInvestmentIdInput`} />
          ),
        }
      : {}),
  } as FilterList<GetB2bIndividualOperationsQuery>);

export const B2bIndividualOperationsList = ({ testId }: { testId: string }) => {
  const CreateButton = !isBoExternal() ? (
    <B2bIndividualOperationCreate
      variant="contained"
      color="info"
      size="small"
      sx={{
        fontWeight: 'bold',
        textTransform: 'capitalize',
      }}
    />
  ) : undefined;

  const resource = useResourceContext();
  const removeFromStore = useRemoveFromStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleTabClick = (tab: IndividualOperationTab) => () => {
    removeFromStore(`${resource}.listParams`);
    const params = new URLSearchParams();
    params.set('tab', tab);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  // Dummy record, because react-admin tabs requires a record to work
  return (
    <RecordContextProvider value={{ id: 1 }}>
      <VerticalTabsLayout>
        {!isBoExternal() && (
          <VerticalTab
            icon={<AllIcon />}
            label="resources.bo_common.b2b.individual_operations.tabs.all.label"
            data-testid={`${testId}_AllTab`}
            path="all"
            onClick={handleTabClick('all')}
            button={CreateButton}>
            <ListView tab={'all'} testId={testId} />
          </VerticalTab>
        )}
        <VerticalTab
          icon={<InProgressIcon />}
          label="resources.bo_common.b2b.campaigns.tabs.in_progress.label"
          data-testid={`${testId}_InProgressTab`}
          path="in_progress"
          onClick={handleTabClick('in_progress')}
          button={CreateButton}>
          <ListView tab={'in_progress'} testId={testId} />
        </VerticalTab>
        <VerticalTab
          icon={<HistoryIcon />}
          label="resources.bo_common.b2b.campaigns.tabs.history.label"
          data-testid={`${testId}_HistoryTab`}
          path="history"
          onClick={handleTabClick('history')}
          button={CreateButton}>
          <ListView tab={'history'} testId={testId} />
        </VerticalTab>
      </VerticalTabsLayout>
    </RecordContextProvider>
  );
};

const ListView = ({ tab, testId }: { tab: IndividualOperationTab; testId: string }) => {
  const resourceBasePath = useResourceBasePath();
  const defaultColumns: (keyof IndividualOperationListRecord)[] = [
    'id',
    'equipment',
    'type',
    'initiator',
    'reference_period',
    'execution_date',
    'status',
    'created_at',
    'updated_at',
  ];

  const b2bIndividualOperationsListColumns: ColumnList<IndividualOperationListRecord> = {
    id: (
      <IdField
        reference={`${resourceBasePath}individual_operations`}
        data-testid={`${testId}_IdField`}
      />
    ),
    b2b_company_plan_id: (
      <IdField<IndividualOperationListRecord>
        source="b2b_company_plan_id"
        reference={(record) =>
          `${resourceBasePath}contracts/${record.b2b_contract_id}/show?tab=plans&product_id=${record.b2b_company_plan_id}`
        }
        data-testid={`${testId}_B2bCompanyPlanIdField`}
      />
    ),
    equipment: (
      <SelectChipField choices={planCategoryChoices} data-testid={`${testId}_EquipmentField`} />
    ),
    type: (
      <SelectChipField
        choices={b2bIndividualOperationTypeChoices}
        data-testid={`${testId}_TypeField`}
      />
    ),
    initiator: (
      <SelectChipField
        choices={b2bIndividualOperationInitiatorChoices}
        data-testid={`${testId}_InitiatorField`}
      />
    ),
    reference_period: <TextField data-testid={`${testId}_ReferencePeriodField`} />,
    execution_date: (
      <UTCDateField
        showTime={!isBoExternal()}
        emptyText="-"
        data-testid={`${testId}_ExecutionDateField`}
      />
    ),
    status: (
      <SelectChipField choices={b2bOrderStatusChoices} data-testid={`${testId}_StatusField`} />
    ),
    cancellation_initiator: (
      <SelectChipField
        choices={b2bCancellationInitiatorChoices}
        emptyText="-"
        data-testid={`${testId}_CancellationInitiatorField`}
      />
    ),
    cancellation_reason: (
      <SelectChipField
        choices={b2bCancellationReasonChoices}
        emptyText="-"
        data-testid={`${testId}_CancellationReasonField`}
      />
    ),
    total_gross_amount: <CurrencyField currency="EUR" testId={`${testId}_TotalGrossAmountField`} />,
    total_entry_fee_employee_amount: (
      <CurrencyField currency="EUR" testId={`${testId}_TotalEntryFeeEmployeeAmountField`} />
    ),
    total_net_amount: <CurrencyField currency="EUR" testId={`${testId}_TotalNetAmountField`} />,
    created_at: (
      <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_CreatedAtField`} />
    ),
    updated_at: (
      <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_UpdatedAtField`} />
    ),
    firstname: <TextField data-testid={`${testId}_FirstnameField`} />,
    lastname: <TextField data-testid={`${testId}_LastnameField`} />,
    identifier: <TextField data-testid={`${testId}_IdentifierField`} />,
    registration_number: <TextField data-testid={`${testId}_RegistrationNumberField`} />,
    availability_date: (
      <UTCDateField
        showTime={false}
        emptyText="-"
        data-testid={`${testId}_AvailabilityDateField`}
      />
    ),
    plan_type: <TextField data-testid={`${testId}_PlanTypeField`} />,
    b2b_employee_id: (
      <IdField
        reference={`${resourceBasePath}employees`}
        data-testid={`${testId}_B2bEmployeeIdField`}
      />
    ),
    b2b_company_id: (
      <IdField
        reference={`${resourceBasePath}companies`}
        data-testid={`${testId}_B2bCompanyIdField`}
      />
    ),
    b2b_contract_id: (
      <IdField
        reference={`${resourceBasePath}contracts`}
        data-testid={`${testId}_B2bContractIdField`}
      />
    ),
    user_id: <IdField reference={'users'} data-testid={`${testId}_UserIdField`} />,
    bo_user_email: <TextField data-testid={`${testId}_BoUserEmailField`} />,
    b2b_recurring_investment_id: (
      <IdField
        reference={`${resourceBasePath}recurring_investments`}
        emptyText="-"
        data-testid={`${testId}_B2bRecurringInvestmentIdField`}
      />
    ),
  };

  const displayedColumns = useSelectedColumns<IndividualOperationListRecord>({
    preferences: 'b2b_individual_operations.list.columns',
    defaultColumns,
  });

  const [activeFilters, setActiveFilters] = useState<Record<string, unknown>>({});

  const statusFilter: OdOrderStatus[] = useMemo(() => {
    const allStatuses = D.keys(statusTabMapping);
    return tab === 'all'
      ? [...allStatuses]
      : allStatuses.filter((status) => statusTabMapping[status] === tab);
  }, [tab]);

  const b2bIndividualOperationsFiltersFinal = useMemo(
    () => b2bIndividualOperationsFilters(statusFilter, isBoExternal()),
    [statusFilter],
  );

  const statusFiltersFinal = useMemo(
    () => (tab === 'all' ? activeFilters.status : (activeFilters.status ?? statusFilter)),
    [activeFilters, statusFilter, tab],
  );

  const selectColumnsTestId = `${testId}_SelectColumnsButton`;
  let columns = b2bIndividualOperationsListColumns;
  if (isBoExternal()) {
    const {
      user_id: _userId,
      b2b_company_id: _companyId,
      b2b_recurring_investment_id: _recurringInvestmentId,
      ...rest
    } = b2bIndividualOperationsListColumns;
    columns = { ...rest } as typeof b2bIndividualOperationsListColumns;
  }
  return (
    <List
      actions={
        <TopToolbar>
          <FilterButtonWithValues onChange={setActiveFilters} />
          <SelectColumnsButton
            preference="b2b_individual_operations.list.columns"
            columns={columns}
            defaultColumns={defaultColumns}
            testId={selectColumnsTestId}
          />
          <ExportButtonWithConfirmation testId={testId} />
        </TopToolbar>
      }
      empty={false}
      filter={{ status: statusFiltersFinal }}
      filters={b2bIndividualOperationsFiltersFinal}
      sort={defaultSort}
      sx={{
        '& .RaList-main': {
          width: '100%',
        },
      }}>
      <DatagridTyped<IndividualOperationListRecord>
        rowClick="show"
        sortableColumns={getB2bIndividualOperationsSortColumns}
        columnRegistry={columns}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
        empty={<NoIndividualOperations />}
      />
    </List>
  );
};

const FilterButtonWithValues = ({
  onChange,
}: {
  onChange: (filters: Record<string, unknown>) => void;
}) => {
  const { filterValues } = useListContext<IndividualOperationListRecord>();

  useEffect(() => {
    onChange(filterValues ?? {});
  }, [filterValues, onChange]);

  return <FilterButton data-testid={`${testId}_FilterButton`} />;
};
